import { useApiUrl, useNotification } from "@refinedev/core";
import { Button, Space, Typography } from "antd";
import { DownloadOutlined, MailOutlined } from "@ant-design/icons";
import axios from "axios";
import FileSaver from "file-saver";
import React from "react";
import { TOKEN_KEY } from "authProvider";
import { Period, periodFileToken, periodQueryParams } from "../lib/period";
import { EmailReportModal } from "./EmailReportModal";

export interface ExportScope {
  scopeType: "all" | "shipco" | "vessel" | "imo-list";
  scopeId?: number;
  imos?: string[];
}

interface ExportButtonsProps {
  scope: ExportScope;
  period: Period;
  // A grey line beside the buttons saying what the export covers. Only the
  // Analytics tab passes one; the vessel drawer and the IMO report do not.
  scopeNote?: string;
}

// With responseType "blob", axios error responses arrive as a Blob too, so
// error?.response?.data?.message is always undefined - the backend's real
// message (e.g. AnalyticsScopeNotFoundException) has to be read out of the
// blob body and JSON-parsed.
const extractDownloadErrorMessage = async (
  error: any
): Promise<string | undefined> => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const message = JSON.parse(text)?.message;
      if (Array.isArray(message)) return message.join(", ");
      if (message) return message;
    } catch {
      // not JSON (or empty) - fall through to error.message below
    }
  }
  return error?.message;
};

// CSV download uses raw axios (bypasses the shared dataProvider axios instance)
// so the guarded /flat-fee-analytics/export endpoint needs a manually attached
// bearer token, same precedent as src/pages/on-demand-reports/list.tsx.
export const ExportButtons: React.FC<ExportButtonsProps> = ({
  scope,
  period,
  scopeNote,
}) => {
  const apiUrl = useApiUrl();
  const { open } = useNotification();
  const [downloading, setDownloading] = React.useState(false);
  const [emailModalOpen, setEmailModalOpen] = React.useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await axios.post(
        `${apiUrl}/flat-fee-analytics/export`,
        { ...scope, ...periodQueryParams(period) },
        { responseType: "blob", headers: { Authorization: `Bearer ${token}` } }
      );
      const dateStamp = new Date().toISOString().slice(0, 10);
      FileSaver.saveAs(
        response.data,
        `flat-fee-report-${periodFileToken(period)}-${dateStamp}.csv`
      );
    } catch (error: any) {
      const description = await extractDownloadErrorMessage(error);
      open?.({
        type: "error",
        message: "Download Failed",
        description:
          description || "An error occurred while downloading the report.",
      });
    }
    setDownloading(false);
  };

  return (
    <>
      <Space wrap>
        {scopeNote && (
          <Typography.Text type="secondary">{scopeNote}</Typography.Text>
        )}
        <Button
          icon={<DownloadOutlined />}
          loading={downloading}
          onClick={handleDownload}
        >
          Export CSV
        </Button>
        <Button icon={<MailOutlined />} onClick={() => setEmailModalOpen(true)}>
          Email report
        </Button>
      </Space>
      <EmailReportModal
        open={emailModalOpen}
        scope={scope}
        period={period}
        onClose={() => setEmailModalOpen(false)}
      />
    </>
  );
};
